import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark, faClouds, faGear, faLaptop, faQuestion } from '@fortawesome/pro-regular-svg-icons';
import { faBookmark as faSolidBookMark } from '@fortawesome/pro-solid-svg-icons';
import { Button, ButtonDropdown, ButtonGroup, NotificationDispatch, StyledFlex, useViewport, VotingButtons } from '@tryhackme/thm-ui-components';
import { AWSCloudDetailsModal } from 'src/features/room/components/room-banner/components/aws-cloud-details/aws-cloud-details-modal';
import { AWSCloudDetailsPopup } from 'src/features/room/components/room-banner/components/aws-cloud-details/aws-cloud-details-popup';
import { useGetCompanyQuery, useGetUserQuery, useTrackEventMutation } from 'src/common/slices';
import { useGetRoomDetailQuery, useGetRoomVotesQuery, useSetRoomVotesMutation } from 'src/features/room/room.slice';
import { useAddSavedRoomMutation, useRemoveSavedRoomMutation } from 'src/features/my-rooms/my-rooms.slice';
import { useGetBadgesQuery } from 'src/features/badges/badges.slice';
import { getHelpOptions } from 'src/features/room/helpers';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { ShowWriteups } from 'src/features/room/components/writeup-modals';
import { ResetProgressModal } from 'src/features/room/components/reset-progress-modal';
import { LeaveRoomModal } from 'src/features/room/components/leave-room-modal';
import { Writeup } from 'src/features/room/components/writeup-modals/writeup-modal.types';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { store } from 'src/app/store';
import { setVmPanelOpen } from 'src/app/reducers/split-screen.reducer';
import { DesktopOnlyAccessPopup } from 'src/features/room/components/desktop-only-access-popup';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { VmTypes } from 'src/common/interfaces/vms';
import { setVmStartType } from 'src/app/reducers/room/room.reducer';
import { useStartVm } from 'src/common/hooks/use-start-vm';
import { TaskType } from 'src/common/enums/task';
import { ROOM_CODE_ATTACK_BOX_FOR_FREE_USERS, ROOM_CODE_ATTACK_BOX_PREMIUM_OR_KALI } from 'src/common/constants/vms';
import { useGetAwsCredentialsQuery, useGetAwsEnvDetailsQuery } from 'src/features/api/rooms/aws/aws.slice';
import { AWSAccountState, UserRoles } from 'src/common/enums';
import { RoomCloudEnvEvent } from 'src/features/room/room.types';
import { RoomUserEventType } from 'src/common/enums/rooms';
import { ROOM_USER_EVENT } from 'src/features/room/room.constants';
import { addSocketListener, removeSocketListener } from 'src/common/sockets';
import { ModalLayout } from 'src/features/manage-room/components';
import { useCloneRoomMutation } from 'src/features/manage-room/manage-room.slice';
import { useIsCurrentTeamOwnerQuery } from 'src/features/manage-account/teams/team.slice';
import { useMediaQuery } from 'src/common/hooks/use-media-query';
import { NetworkInstanceStatus } from 'src/common/interfaces/networks/instances';
import { useRequestAzureLabMutation } from 'src/features/admin/cloud-training/azure-tenant-onboarding.slice';
import { AWSCredentials, EnvironmentStatusOptions } from '../aws-cloud-details/aws-cloud-details.types';
import { BROWSER_MACHINE_OPTIONS, HELPOPTIONSDROPDOWN, OPTIONS, OPTIONSDROPDOWN } from '../../room-banner.constants';
import { StyledLoading } from '../loading/loading.styles';
import { AwardsBadgePopUp } from '../../../awards-badge-popup';
import { Certificate } from '../certificates';
import { CompletionShareRoomModal } from '../completion-modal-share-roomcompletion-share-room-modal';
import { ShareAoCModal } from '../share-aoc-modal';
import { StyledButton, StyledChildButton, StyledWrapperAttackBoxButton } from './room-banner-actions.styles';
import { RoomBannerActionsProps } from './room-banner-actions.type';
export const RoomBannerActions = ({
  roomDetails,
  isLoading,
  isRetiredRoom,
  isPrivateOrRetiredRoom,
  setRoomProgress,
  roomProgress,
  setShowVideo,
  splitScreenTabs,
  vmStartType,
  resetToFirstTask,
  isTaskBreakdownSupported
}: RoomBannerActionsProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    roomCode
  } = useParams();
  if (!roomCode) throw new Error('No roomCode in url params');
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [openResetProgressModal, setOpenResetProgressModal] = useState<boolean>(false);
  const [openLeaveRoomModal, setOpenLeaveRoomModal] = useState<boolean>(false);
  const [openCloneRoomModal, setOpenCloneRoomModal] = useState<boolean>(false);
  const [leaveAsTeam, setLeaveAsTeam] = useState<boolean>(false);
  const [openWriteupsModal, setOpenWriteupsModal] = useState<boolean>(false);
  const [isOpenDesktopOnlyAccessPopUp, setIsOpenDesktopOnlyAccessPopUp] = useState<boolean>(false);
  const [isOpenAWSCloudDetailsPopup, setIsOpenAWSCloudDetailsPopup] = useState<boolean>(false);
  const [isOpenAWSCloudDetailsModal, setIsOpenAWSCloudDetailsModal] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareAoCModalOpen, setIsShareAoCModalOpen] = useState(false);
  const [awsEnvironmentStatus, setAwsEnvironmentStatus] = useState<EnvironmentStatusOptions>(EnvironmentStatusOptions.STARTING);
  const [awsEnvironmentRoomsList, setAwsEnvironmentRoomsList] = useState<string[]>([]);
  const [awsCredential, setAwsCredential] = useState<AWSCredentials>();
  const isAttackBoxRunning = splitScreenTabs.some(tab => tab.type === TaskType.VM && tab.vm && [ROOM_CODE_ATTACK_BOX_PREMIUM_OR_KALI, ROOM_CODE_ATTACK_BOX_FOR_FREE_USERS].includes(tab.vm.roomCode));
  const {
    refetch: refetchDetails
  } = useGetRoomDetailQuery(roomCode);
  const {
    data: votesData,
    refetch
  } = useGetRoomVotesQuery(roomCode);
  const {
    data: badgesData
  } = useGetBadgesQuery();
  const {
    data: userData,
    isSuccess: isUserAuth
  } = useGetUserQuery();
  const companyId = userData?.data?.user.subscriptions[0]?.companyId;
  const {
    data: companyData
  } = useGetCompanyQuery(companyId ?? '', {
    skip: !companyId
  });
  const {
    startVmHandler,
    isVmStartLoading
  } = useStartVm(roomCode);
  const [setRoomVotes] = useSetRoomVotesMutation();
  const [removeSavedRoom] = useRemoveSavedRoomMutation();
  const [addSavedRoom] = useAddSavedRoomMutation();
  const [trackEvent] = useTrackEventMutation();
  const [cloneRoom] = useCloneRoomMutation();
  const [searchParams] = useSearchParams();
  const [requestAzureLab] = useRequestAzureLabMutation();
  const awardedBadge = badgesData?.data?.badges?.find(badge => badge.toEarn?.includes(roomDetails?.code || ''));
  const savedRoom = roomDetails?.saved;
  const writeupsArray: Writeup[] = roomDetails?.writeups ?? [];
  const showButton = !isPrivateOrRetiredRoom && isUserAuth;
  const hasUserJoinedRoom = roomDetails?.joined;
  const remAttackBoxBtn = roomDetails?.options?.remAttackBoxBtn;
  const showMachineButton = isUserAuth && hasUserJoinedRoom && !remAttackBoxBtn && !isAttackBoxRunning;
  const isAwsCloudRoom = roomDetails?.cloud?.enabled ?? false;
  const userCanCloneRoom = roomDetails?.cloneable && userData?.data?.user?.roles?.includes(UserRoles.ROOM_MAKER);
  const isResetProgressEnabled = roomDetails?.options?.isResetProgressEnabled;
  const {
    data: isTeamOwnerData
  } = useIsCurrentTeamOwnerQuery();
  const isTeamOwner = isTeamOwnerData?.data;
  const isTeamsRoom = !!roomDetails?.teams && roomDetails.joined;
  const areChangesInTeamLocked = roomDetails?.teamSettings?.lockChanges;
  const isAzureCloudRoom = roomDetails?.cloud?.azure?.isEnabled ?? false;
  const isAzureTenantLabsReady = companyData?.additionalContent?.azureTenantLabs?.labsReady;
  const isAzureTenantLabsRequested = companyData?.additionalContent?.azureTenantLabs?.labsRequested;
  const disableRequestTenantEnvButton = isAzureTenantLabsReady || isAzureTenantLabsRequested;
  const loadingRequestTenantEnvButton = isAzureTenantLabsRequested && !isAzureTenantLabsReady;
  const {
    vmPanelOpen
  } = useAppSelector(state => state.splitScreen);
  const {
    tutorPanelOpen
  } = useAppSelector(state => state.splitScreen);
  const networkDetails = useAppSelector(state => state.room.networkDetails);
  const isSplitScreenOpen = tutorPanelOpen || vmPanelOpen;
  const isTablet = useMediaQuery(`(min-width: ${BREAKPOINTS.TABLET}px)`);

  // Force user to start a network v2 first so VPN server can come up for attack box to connect
  const isNetworkV2NotReady = Boolean(roomDetails?.networkId && networkDetails?.status !== NetworkInstanceStatus.RUNNING);
  let FILTERED_OPTIONS = OPTIONS.filter(option => {
    switch (option.value) {
      case OPTIONSDROPDOWN.RESET_PROGRESS:
        {
          return isResetProgressEnabled;
        }
      case OPTIONSDROPDOWN.CLONE_ROOM:
        {
          return userCanCloneRoom;
        }
      case OPTIONSDROPDOWN.LEAVE_AS_TEAM:
        {
          return isTeamsRoom && !!isTeamOwner;
        }
      default:
        {
          return true;
        }
    }
  });
  if (isTeamsRoom) {
    FILTERED_OPTIONS = FILTERED_OPTIONS.map(option => {
      if (option.value === OPTIONSDROPDOWN.LEAVE) {
        return {
          ...option,
          title: 'Leave as a member'
        };
      }
      return option;
    });
  }

  // viewport
  const {
    width
  } = useViewport();
  const executeVmClickAction = async () => {
    if (width < BREAKPOINTS.VM) {
      setIsOpenDesktopOnlyAccessPopUp(true);
    } else {
      await startVmHandler(vmStartType);
    }
  };
  const handleStartMachine = () => {
    // eslint-disable-next-line no-void
    void executeVmClickAction();
  };
  const handlerVote = async (newVote: boolean) => {
    if (roomDetails?.code) {
      await setRoomVotes({
        roomCode: roomDetails?.code,
        vote: newVote
      });
      await refetch();
    }
  };
  const handleSavedRoom = useCallback(async () => {
    try {
      if (savedRoom) {
        await removeSavedRoom(roomCode);
        await refetchDetails();
        NotificationDispatch('success', <>
            Success! Removed from <Link to="/rooms">My Rooms</Link>
          </>);
      } else {
        await addSavedRoom(roomCode);
        await refetchDetails();
        NotificationDispatch('success', <>
            Success! Saved to <Link to="/rooms">My Rooms</Link>
          </>);
      }
    } catch (error: unknown) {
      if (isQueryErrorWithMessage(error)) {
        if (error.status === 400) {
          NotificationDispatch('error', error.data.message);
        } else {
          NotificationDispatch('error', 'Something went wrong. Please try again.');
        }
      }
    }
  }, [savedRoom, removeSavedRoom, roomCode, refetchDetails, addSavedRoom]);
  const {
    data: cloudAwsCredentials,
    isSuccess: isCloudAwsUser
  } = useGetAwsCredentialsQuery(undefined, {
    skip: !roomDetails?.cloud?.enabled
  });
  const {
    data: cloudAwsEnvDetails,
    refetch: refetchEnvDetails
  } = useGetAwsEnvDetailsQuery(roomDetails?._id ?? '', {
    skip: !roomDetails?.cloud?.enabled
  });
  useEffect(() => {
    if (!roomDetails?.cloud) return;
    if (!cloudAwsEnvDetails?.data?.state) return;
    setAwsEnvironmentRoomsList(cloudAwsEnvDetails.data.otherRooms.length > 0 ? cloudAwsEnvDetails.data.otherRooms.map(otherRoom => otherRoom.title) : []);
    switch (cloudAwsEnvDetails.data?.state) {
      case AWSAccountState.IN_USE:
        {
          if (cloudAwsEnvDetails.data.otherRooms.length > 0 && cloudAwsEnvDetails.data.changeEnvironment) {
            setAwsEnvironmentStatus(EnvironmentStatusOptions.ANOTHER_ENVIRONMENT_ACTIVE);
          } else if (cloudAwsEnvDetails.data.otherRooms.length > 0) {
            setAwsEnvironmentStatus(EnvironmentStatusOptions.ACTIVE);
          } else {
            setAwsEnvironmentStatus(EnvironmentStatusOptions.ACTIVE_BUT_EMPTY);
          }
          break;
        }
      case AWSAccountState.ENV_GEN:
        {
          if (cloudAwsEnvDetails.data.otherRooms.length > 0) {
            setAwsEnvironmentStatus(EnvironmentStatusOptions.GENERATING);
          } else {
            setAwsEnvironmentStatus(EnvironmentStatusOptions.GENERATING_FIRST_ENVIRONMENT);
          }
          break;
        }
      case AWSAccountState.RESETTING:
        {
          setAwsEnvironmentStatus(EnvironmentStatusOptions.RESETTING);
          break;
        }
      default:
        {
          break;
        }
    }
  }, [cloudAwsEnvDetails, roomDetails?.cloud]);
  useEffect(() => {
    if (!roomDetails?.cloud) return;
    const handleCloudEnvEvent = (data: unknown) => {
      const eventData = (data as RoomCloudEnvEvent);
      if (eventData.type === RoomUserEventType.ENVIRONMENT_GENERATION) {
        setIsOpenAWSCloudDetailsPopup(true);
      }
    };
    if (roomDetails?.joined) {
      addSocketListener(ROOM_USER_EVENT, handleCloudEnvEvent);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      removeSocketListener(ROOM_USER_EVENT);
    };
  }, [roomDetails, dispatch]);
  useEffect(() => {
    if (isCloudAwsUser) {
      const credentials = {
        urlLink: cloudAwsCredentials?.data?.accountId ? `https://${cloudAwsCredentials.data.accountId}.signin.aws.amazon.com/console/` : '',
        username: cloudAwsCredentials?.data?.accountId || '',
        accessKeyId: cloudAwsCredentials?.data?.credentials.accessKeyId || '',
        secretAccessKey: cloudAwsCredentials?.data?.credentials.secretKey || '',
        defaultConsolePassword: cloudAwsCredentials?.data?.credentials.defaultPassword || ''
      };
      setAwsCredential(credentials);
    }
  }, [cloudAwsCredentials?.data?.accountId, cloudAwsCredentials?.data?.credentials.accessKeyId, cloudAwsCredentials?.data?.credentials.defaultPassword, cloudAwsCredentials?.data?.credentials.secretKey, isCloudAwsUser]);
  useEffect(() => {
    if (searchParams.get('reveal') === 'share') {
      setIsShareModalOpen(true);
      setIsShareAoCModalOpen(true);
    }
  }, [searchParams]);
  const handleCloudDetails = useCallback(async () => {
    if (!roomDetails?.cloud || !roomDetails?._id) return;
    setIsOpenAWSCloudDetailsModal(true);
    await refetchEnvDetails();
  }, [refetchEnvDetails, roomDetails]);
  const selectOption = (value: string) => {
    setSelectedOption(value);
    switch (value) {
      case OPTIONSDROPDOWN.RESET_PROGRESS:
        {
          setOpenLeaveRoomModal(false);
          setOpenResetProgressModal(true);
          break;
        }
      case OPTIONSDROPDOWN.LEAVE_AS_TEAM:
        {
          setLeaveAsTeam(true);
          setOpenResetProgressModal(false);
          setOpenLeaveRoomModal(true);
          break;
        }
      case OPTIONSDROPDOWN.LEAVE:
        {
          setOpenResetProgressModal(false);
          setOpenLeaveRoomModal(true);
          break;
        }
      case OPTIONSDROPDOWN.CLONE_ROOM:
        {
          setOpenCloneRoomModal(true);
          break;
        }
      default:
        {
          break;
        }
    }
    setSelectedOption('');
  };
  const handleRequestTenantEnv = async () => {
    if (!companyId) return;
    await requestAzureLab({
      companyId
    }).unwrap().then(() => {
      NotificationDispatch('success', 'Tenant environment requested');
    }).catch(error => {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error?.data?.message : 'Something went wrong.');
    });
  };
  const handleCloneRoom = async () => {
    if (!roomDetails?._id) return;
    await cloneRoom(roomDetails._id).unwrap().then(res => {
      const clonedCode = res.data?.clonedCode;
      setOpenCloneRoomModal(false);
      NotificationDispatch('success', 'Room successfully cloned');
      // eslint-disable-next-line no-void
      void trackEvent({
        event: 'clone-room',
        properties: {
          room: roomCode
        }
      });
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      navigate(`/room/manage/${clonedCode}`);
    }).catch(error => {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error?.data?.message : 'Something went wrong.');
      setOpenCloneRoomModal(false);
    });
  };
  const selectHelpOption = (value: string) => {
    setSelectedOption(value);
    if (value === HELPOPTIONSDROPDOWN.WRITEUPS) {
      setOpenWriteupsModal(true);
    } else if (value === HELPOPTIONSDROPDOWN.VIDEO_SOLUTION) {
      setShowVideo(true);
      setOpenWriteupsModal(false);
    }
    setSelectedOption('');
  };
  const onHelpMenuOpenChange = (menuOpen: boolean) => {
    if (menuOpen) {
      // eslint-disable-next-line no-void
      void trackEvent({
        event: 'open-help-menu',
        properties: {
          room_code: roomCode
        }
      });
    }
  };
  const showHelpOptions = useMemo(() => ({
    isVideoSolutionOptionVisible: roomDetails?.video?.free ? roomDetails?.video?.active && roomDetails?.simpleRoom : roomDetails?.video?.active && userData?.data?.user.isPremium && roomDetails?.simpleRoom,
    isWriteupsOptionVisible: !!roomDetails?.writeups?.length && roomDetails?.simpleRoom
  }), [roomDetails?.video?.free, roomDetails?.video?.active, roomDetails?.writeups?.length, userData?.data?.user.isPremium, roomDetails?.simpleRoom]);
  const handleModalOpen = (open: boolean) => {
    setIsShareModalOpen(open);
    if (open) {
      navigate(`?reveal=share`, {
        replace: true
      });
    } else {
      navigate('', {
        replace: true
      });
    }
  };
  const handleShareAoCModal = (modalOpen: boolean) => {
    setIsShareAoCModalOpen(modalOpen);
    // eslint-disable-next-line no-void
    void trackEvent({
      event: 'click-AoC-CTA',
      properties: {
        room_code: roomCode,
        type: 'room',
        name: roomCode,
        medium: 'AoC-CTA'
      }
    });
    navigate(modalOpen ? '?reveal=share' : '', {
      replace: true
    });
  };
  const renderActionsForRoomWithTaskBreakdownSupport = () => <StyledFlex gap="1.6rem" data-sentry-element="StyledFlex" data-sentry-component="renderActionsForRoomWithTaskBreakdownSupport" data-sentry-source-file="room-banner-actions.tsx">
      {!vmPanelOpen && splitScreenTabs.length > 0 && <Button onClick={() => {
      store.dispatch(setVmPanelOpen(true));
    }} color="add">
          {' '}
          Show Split View
        </Button>}
      {showButton && <StyledButton variant="outlined" color="secondary"
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onClick={() => handleSavedRoom()}>
          <FontAwesomeIcon icon={roomDetails?.saved ? faSolidBookMark : faBookmark} />
          {!isSplitScreenOpen && isTablet && 'Save Room'}
        </StyledButton>}
      <ButtonDropdown isDarkVersion options={getHelpOptions(showHelpOptions)} onOptionSelect={(value: string) => selectHelpOption(value)} onOpenChange={onHelpMenuOpenChange} selectedOption={selectedOption} alignContent="start" buttonLabel={!isSplitScreenOpen && isTablet ? 'Help' : ''} icon={<FontAwesomeIcon icon={faQuestion} />} data-sentry-element="ButtonDropdown" data-sentry-source-file="room-banner-actions.tsx" />
      {!isRetiredRoom && isUserAuth && hasUserJoinedRoom && <ButtonDropdown isDarkVersion options={FILTERED_OPTIONS} selectedOption={selectedOption} alignContent="start" buttonLabel={!isSplitScreenOpen && isTablet ? 'Options' : ''} onOptionSelect={(value: string) => selectOption(value)} icon={<FontAwesomeIcon icon={faGear} />} />}
      <LeaveRoomModal open={openLeaveRoomModal} onOpenChange={setOpenLeaveRoomModal} roomCode={roomCode} asTeam={leaveAsTeam} isTeamsRoom={isTeamsRoom} areChangesInTeamLocked={areChangesInTeamLocked} onClosed={() => {
      setLeaveAsTeam(false);
    }} data-sentry-element="LeaveRoomModal" data-sentry-source-file="room-banner-actions.tsx" />
      <ResetProgressModal open={openResetProgressModal} onOpenChange={setOpenResetProgressModal} roomCode={roomCode} setRoomProgress={setRoomProgress} resetToFirstTask={resetToFirstTask} data-sentry-element="ResetProgressModal" data-sentry-source-file="room-banner-actions.tsx" />
    </StyledFlex>;
  if (isTaskBreakdownSupported) {
    return renderActionsForRoomWithTaskBreakdownSupport();
  }
  return <StyledFlex justifyContent="flex-start" gap="1.2rem" data-testid="actions" data-sentry-element="StyledFlex" data-sentry-component="RoomBannerActions" data-sentry-source-file="room-banner-actions.tsx">
      {isLoading ? <>
          <StyledLoading width="21.3rem" data-testid="loading-1" />
          <StyledLoading width="8.7rem" />
          <StyledLoading width="13.4rem" />
          <StyledLoading width="11.4rem" />
        </> : <>
          {roomProgress === 100 && isUserAuth && <CompletionShareRoomModal roomCode={roomDetails?.code ?? ''} roomType={roomDetails?.type ?? 'challenge'} isOpenModal={isShareModalOpen} isShareModalOpen={isShareModalOpen} onOpenChange={handleModalOpen} setIsShareModalOpen={setIsShareModalOpen} />}
          {roomCode === 'adventofcyber2024' && roomProgress !== 100 && <ShareAoCModal roomCode={roomDetails?.code ?? 'adventofcyber2024'} isShareAoCModalOpen={isShareAoCModalOpen} setIsShareAoCModalOpen={setIsShareAoCModalOpen} onOpenChange={handleShareAoCModal} />}
          {!vmPanelOpen && splitScreenTabs.length > 0 && <Button onClick={() => {
        store.dispatch(setVmPanelOpen(true));
      }} color="add">
              {' '}
              Show Split View
            </Button>}
          {roomDetails?.certificate && isUserAuth && <Certificate roomCode={roomCode} roomProgress={roomProgress} />}
          {showMachineButton && <StyledWrapperAttackBoxButton id="start-attackbox">
              <ButtonGroup isDarkVersion>
                <StyledChildButton color="secondary" variant="outlined" type="button" key="three" onClick={handleStartMachine} isLoading={isVmStartLoading} loadingMsg="Starting" data-testid="start-attack-box-btn" disabled={isNetworkV2NotReady}>
                  <FontAwesomeIcon icon={faLaptop} /> {isAttackBoxRunning ? `Show Split View` : `Start ${vmStartType}`}
                </StyledChildButton>
                {!isNetworkV2NotReady && <ButtonDropdown isDarkVersion options={BROWSER_MACHINE_OPTIONS} onOptionSelect={(value: string) => {
            dispatch(setVmStartType((value as VmTypes)));
          }} selectedOption={vmStartType} alignContent="end" />}
                <DesktopOnlyAccessPopup open={isOpenDesktopOnlyAccessPopUp} onOpenChange={setIsOpenDesktopOnlyAccessPopUp} closeAction={() => {
            // eslint-disable-next-line no-void
            void startVmHandler(vmStartType);
          }} splitScreenType={TaskType.VM} />
              </ButtonGroup>
            </StyledWrapperAttackBoxButton>}
          {Boolean(awardedBadge) && <AwardsBadgePopUp awardedBadge={awardedBadge} />}
          {isUserAuth && isAwsCloudRoom &&
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      <StyledButton variant="outlined" color="secondary" onClick={() => handleCloudDetails()}>
              <FontAwesomeIcon icon={faClouds} style={{
          width: 'auto'
        }} />
              Cloud Details
            </StyledButton>}
          <ButtonDropdown isDarkVersion options={getHelpOptions(showHelpOptions)} onOptionSelect={(value: string) => selectHelpOption(value)} onOpenChange={onHelpMenuOpenChange} selectedOption={selectedOption} alignContent="start" buttonLabel="Help" />
          {showButton && <StyledButton variant="outlined" color="secondary"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={() => handleSavedRoom()}>
              <FontAwesomeIcon icon={savedRoom ? faSolidBookMark : faBookmark} />
              Save Room
            </StyledButton>}
          {!isPrivateOrRetiredRoom && <VotingButtons
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onChange={newVote => handlerVote(newVote)} votesCount={votesData?.data?.votes} userVote={votesData?.data?.userVote} isAuth={isUserAuth} isDisabled={!isUserAuth || !hasUserJoinedRoom} />}
          {!isRetiredRoom && isUserAuth && hasUserJoinedRoom && <ButtonDropdown isDarkVersion options={FILTERED_OPTIONS} selectedOption={selectedOption} alignContent="start" buttonLabel="Options" onOptionSelect={(value: string) => selectOption(value)} icon={<FontAwesomeIcon icon={faGear} />} />}
          {isAzureCloudRoom && <Button variant="solid" color="primary" isDisabled={disableRequestTenantEnvButton} isLoading={loadingRequestTenantEnvButton} loadingMsg="Loading tenant environment"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={handleRequestTenantEnv}>
              <FontAwesomeIcon icon={faClouds} />
              {isAzureTenantLabsReady ? 'Tenant ready' : 'Request tenant environment'}
            </Button>}
          {openCloneRoomModal && <ModalLayout showTriggerElement={false} open={openCloneRoomModal} openChange={setOpenCloneRoomModal} showWarnBox={false} actionButtonText="Clone room" modalTitleText="Clone room" closeButtonText="Close"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      handleActionButton={handleCloneRoom}>
              <p>Are you sure you want to clone this room?</p>
            </ModalLayout>}
          <ResetProgressModal open={openResetProgressModal} onOpenChange={setOpenResetProgressModal} roomCode={roomCode} setRoomProgress={setRoomProgress} resetToFirstTask={resetToFirstTask} />
          <LeaveRoomModal open={openLeaveRoomModal} onOpenChange={setOpenLeaveRoomModal} roomCode={roomCode} asTeam={leaveAsTeam} isTeamsRoom={isTeamsRoom} areChangesInTeamLocked={areChangesInTeamLocked} onClosed={() => {
        setLeaveAsTeam(false);
      }} />
          <AWSCloudDetailsModal open={isOpenAWSCloudDetailsModal} onOpenChange={setIsOpenAWSCloudDetailsModal} environmentStatus={awsEnvironmentStatus} roomsList={awsEnvironmentRoomsList} awsCredentials={awsCredential} roomDetails={roomDetails} />
          <AWSCloudDetailsPopup open={isOpenAWSCloudDetailsPopup} onOpenChange={setIsOpenAWSCloudDetailsPopup} />
          <ShowWriteups open={openWriteupsModal} onOpenChange={setOpenWriteupsModal} roomCode={roomCode} writeupsArray={writeupsArray} />
        </>}
    </StyledFlex>;
};